/**
 * BriefBrowser — first look at the 300 canonical opportunity briefs.
 *
 * Designed as ugly-but-functional: a sortable list with filters + a detail
 * panel that surfaces the trust moat (sources, honest deferrals, cluster
 * members) prominently.
 *
 * Wires to /api/discover/briefs.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import RevenueProofCard from "../components/RevenueProofCard";
import GuideHelpButton from "../components/GuideHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_NAMES = {
  AU: "Australia", CA: "Canada", DE: "Germany", DK: "Denmark", EE: "Estonia",
  ES: "Spain", FR: "France", GB: "United Kingdom", IE: "Ireland", IL: "Israel",
  IN: "India", NL: "Netherlands", NZ: "New Zealand", SG: "Singapore", US: "United States",
};

const INDUSTRY_LABELS = {
  ai_ml: "AI/ML", climate: "Climate", consumer: "Consumer",
  ecommerce_marketplace: "E-commerce/Marketplace", education: "Education",
  fintech: "Fintech", healthcare_bio: "Healthcare/Bio",
  industrials_dynamism: "Industrials", legal_tech: "Legal Tech",
  productivity: "Productivity", real_estate_proptech: "Real Estate/PropTech",
  saas_b2b: "SaaS B2B",
};

const SECTION_LABELS = {
  the_play: "The Play",
  why_now: "Why Now",
  who_pays: "Who Pays",
  the_wedge: "The Wedge",
  regulatory_edge: "Regulatory Edge",
  cac_bet: "CAC Bet",
  proof_it_travels: "Proof It Travels",
  kill_risk: "Kill Risk",
};

// Render a brief text block with inline [source: URL] markers turned into
// clickable superscript citations. Honest-deferral phrases get a soft amber tag.
function BriefSection({ label, text }) {
  if (!text) return null;
  const isDeferral = /No grounded|verify before acting/i.test(text);
  // Replace [source: URL] inline citations with clickable superscripts.
  const parts = [];
  const re = /\[source:\s*(https?:\/\/[^\]\s]+)\]/g;
  let lastIdx = 0; let m; let citeIdx = 0;
  while ((m = re.exec(text)) !== null) {
    parts.push(text.slice(lastIdx, m.index));
    citeIdx += 1;
    const url = m[1];
    parts.push(
      <a
        key={`${label}-cite-${citeIdx}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block align-super text-[10px] font-mono ml-0.5 px-1 py-0.5 rounded bg-violet-100 text-violet-700 hover:bg-violet-200"
        data-testid={`brief-inline-cite-${citeIdx}`}
        title={url}
      >
        [{citeIdx}]
      </a>
    );
    lastIdx = re.lastIndex;
  }
  parts.push(text.slice(lastIdx));
  return (
    <div className="mb-5" data-testid={`brief-section-${label}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <h4 className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">
          {SECTION_LABELS[label] || label}
        </h4>
        {isDeferral && (
          <span
            className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 cursor-help"
            title="⚐ Honest deferral — we found no groundable source for this claim, so we flagged it instead of fabricating. Verify before acting. This is the moat."
            data-testid={`brief-deferral-${label}`}
          >
            ⚐ honest deferral
          </span>
        )}
      </div>
      <p className="text-sm leading-relaxed text-zinc-800">{parts}</p>
    </div>
  );
}

function TierBadge({ tier }) {
  if (tier === "free") {
    return (
      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
        free
      </span>
    );
  }
  return (
    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
      pro
    </span>
  );
}

function ConfidenceTier({ composite }) {
  let label = "low"; let cls = "bg-zinc-100 text-zinc-600";
  if (composite >= 0.65) { label = "high"; cls = "bg-emerald-100 text-emerald-700"; }
  else if (composite >= 0.55) { label = "med"; cls = "bg-amber-100 text-amber-700"; }
  return (
    <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>
      conf · {label}
    </span>
  );
}

function BriefDetailPanel({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    setData(null); setErr(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/discover/briefs/${id}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  if (err) return <div className="p-6 text-red-600 text-sm" data-testid="brief-detail-error">Failed to load brief: {err}</div>;
  if (!data) return <div className="p-6 text-zinc-500 text-sm font-mono" data-testid="brief-detail-loading">Loading brief…</div>;

  const brief = data.brief || {};

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-lg" data-testid="brief-detail-panel">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-zinc-100 px-5 sm:px-6 py-4 rounded-t-xl flex items-start justify-between gap-3 z-10">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <TierBadge tier={data.tier} />
            <ConfidenceTier composite={data.calibration_composite} />
            {data.regrounded && (
              <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700"
                title="Re-grounded with broader-query pass"
              >re-grounded</span>
            )}
            <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">
              gap · {data.gap_score}
            </span>
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-zinc-900 leading-tight" data-testid="brief-detail-title">
            {data.centroid_name} → {COUNTRY_NAMES[data.missing_country] || data.missing_country}
          </h2>
          <div className="text-xs text-zinc-500 mt-0.5">
            {INDUSTRY_LABELS[data.industry_bucket] || data.industry_bucket} · {data.cluster_label}
            {' · '}centroid focus: <span className="font-mono">{data.centroid_geographic_focus}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-zinc-500 hover:text-zinc-900 text-xl leading-none px-2 py-1 rounded hover:bg-zinc-100"
          data-testid="brief-detail-close"
          aria-label="Close brief"
        >×</button>
      </div>

      {/* Body */}
      <div className="p-5 sm:p-6">
        {/* GAP DETECTED — Provenance block. The wow opener. */}
        <div className="mb-5 rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-white overflow-hidden" data-testid="brief-provenance">
          <div className="px-4 py-2.5 bg-violet-600 text-white text-[10px] font-mono uppercase tracking-[0.22em] flex items-center justify-between">
            <span>◎ Gap detected</span>
            <span className="text-violet-200">gap score {data.gap_score}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-violet-200">
            <div className="p-3.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Centroid (US-validated)</div>
              <div className="text-[15px] font-bold text-zinc-900">{data.centroid_name}</div>
              <div className="text-[11px] text-zinc-600 mt-0.5">{INDUSTRY_LABELS[data.industry_bucket] || data.industry_bucket} · {data.centroid_geographic_focus}</div>
            </div>
            <div className="p-3.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Replicated by</div>
              <div className="text-[15px] font-bold text-zinc-900">{data.cluster_member_count} similar companies</div>
              <div className="text-[11px] text-zinc-600 mt-0.5">cosine ≥ 0.75 · YC / a16z / Accel / Sequoia</div>
            </div>
            <div className="p-3.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Missing in</div>
              <div className="text-[15px] font-bold text-violet-700">{COUNTRY_NAMES[data.missing_country] || data.missing_country}</div>
              <div className="text-[11px] text-zinc-600 mt-0.5">0 grounded equivalents in 4,593-co corpus</div>
            </div>
          </div>
        </div>

        {/* The Play — hero */}
        {brief.the_play && (
          <div className="mb-6 p-4 rounded-lg bg-violet-50 border border-violet-100">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-violet-700 mb-1">The Play</div>
            <p className="text-sm sm:text-[15px] font-medium leading-relaxed text-zinc-900" data-testid="brief-the-play">
              {brief.the_play}
            </p>
          </div>
        )}

        {/* Remaining sections */}
        {["why_now", "who_pays", "the_wedge", "regulatory_edge", "cac_bet", "proof_it_travels", "kill_risk"].map(k => (
          <BriefSection key={k} label={k} text={brief[k]} />
        ))}

        {/* Revenue proof — real indie products already earning in this market */}
        <div className="mt-6">
          <RevenueProofCard proof={data.revenue_proof} />
        </div>

        {/* Sources — the trust moat */}
        <div className="mt-6 p-4 rounded-lg bg-zinc-50 border border-zinc-200" data-testid="brief-sources-block">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-700">
              Sources · the trust moat
            </div>
            <span className="text-[10px] font-mono text-zinc-500">
              {data.source_count} cited · {data.honest_deferral_count} ⚐ deferred
            </span>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
            Every hard claim above is either cited below or visibly marked ⚐. We refuse to fabricate.
          </p>
          {data.sources && data.sources.length > 0 ? (
            <ol className="space-y-2 list-decimal list-inside">
              {data.sources.map((s, i) => (
                <li key={i} className="text-sm text-zinc-700">
                  <span className="text-zinc-600">{s.claim}</span>
                  {s.source_url && (
                    <>
                      {' — '}
                      <a
                        href={s.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-violet-700 hover:text-violet-900 underline break-all"
                        data-testid={`brief-source-link-${i}`}
                      >{s.source_url}</a>
                    </>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-xs text-amber-700 font-mono bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚐ This brief carries zero external sources — every section above is a visible honest deferral. Treat the strategic reasoning (wedge, who-pays, CAC bet) as the value; verify every market-fact before acting.
            </div>
          )}
        </div>

        {/* CLUSTER RECEIPTS — the drill-down. The "show me the work" moment. */}
        {data.cluster_members && data.cluster_members.length > 0 && (
          <div className="mt-6 rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="brief-cluster-members">
            <div className="px-4 py-2.5 bg-zinc-900 text-white flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em]">
                ▤ Cluster receipts · {data.cluster_member_count} similar companies
              </div>
              <div className="text-[9px] font-mono text-zinc-400">
                showing {data.cluster_members.length}
              </div>
            </div>
            <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="cluster-receipts-grid">
              {data.cluster_members.map((m, i) => (
                <div key={i} className="rounded border border-zinc-200 hover:border-violet-300 bg-white px-3 py-2.5 transition-colors" data-testid={`cluster-receipt-${i}`}>
                  <div className="flex items-start gap-2.5">
                    {m.logo_url ? (
                      <img src={m.logo_url} alt="" className="w-9 h-9 rounded flex-shrink-0 bg-zinc-100 object-contain" loading="lazy" />
                    ) : (
                      <div className="w-9 h-9 rounded bg-zinc-100 flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-zinc-500">
                        {m.name?.[0] || '?'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {m.url ? (
                          <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-zinc-900 hover:text-violet-700 truncate">
                            {m.name}
                          </a>
                        ) : (
                          <span className="text-sm font-semibold text-zinc-900 truncate">{m.name}</span>
                        )}
                        {m.source && (
                          <span className="text-[8px] font-mono uppercase px-1 py-0.5 rounded bg-violet-100 text-violet-700">
                            {m.source === 'ycombinator' ? 'YC' : m.source}
                          </span>
                        )}
                        {m.acquired && (
                          <span className="text-[8px] font-mono uppercase px-1 py-0.5 rounded bg-emerald-100 text-emerald-700" title={m.acquired_by ? `Acquired by ${m.acquired_by}` : 'Exited'}>
                            ✓ exit
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 mt-0.5 flex-wrap">
                        {m.founded_year && <span>est {m.founded_year}</span>}
                        {m.team_size && <span>· {m.team_size} ppl</span>}
                        {m.country && <span>· {m.country}{m.city ? `, ${m.city}` : ''}</span>}
                      </div>
                      {m.description && (
                        <p className="text-[11px] text-zinc-600 mt-1 line-clamp-2 leading-snug">{m.description}</p>
                      )}
                      {m.lead_founder_name && (
                        <div className="text-[10px] text-zinc-500 mt-1">
                          <span className="font-mono text-zinc-400">founder ·</span>{' '}
                          {m.lead_founder_linkedin ? (
                            <a href={m.lead_founder_linkedin} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:underline">
                              {m.lead_founder_name}
                            </a>
                          ) : (
                            <span>{m.lead_founder_name}</span>
                          )}
                          {m.lead_founder_title && <span className="text-zinc-400"> · {m.lead_founder_title}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-200 text-[10px] font-mono text-zinc-500 flex flex-wrap items-center justify-between gap-2">
              <span>These are the receipts. We clustered {data.cluster_member_count} real companies before we wrote a single word above.</span>
              {data.cluster_member_count > data.cluster_members.length && (
                <span className="text-zinc-400">+ {data.cluster_member_count - data.cluster_members.length} more in cluster</span>
              )}
            </div>
          </div>
        )}

        {/* Provenance footer */}
        <div className="mt-5 text-[10px] font-mono text-zinc-400 flex flex-wrap gap-3">
          <span>cluster #{data.cluster_id}</span>
          <span>brief_rank {data.brief_rank}</span>
          <span>pick_rank {data.pick_rank}</span>
          <span>composite {data.calibration_composite}</span>
        </div>
      </div>
    </div>
  );
}

function FilterChips({ values, selected, onToggle, labelMap, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map(v => {
        const active = selected.includes(v);
        return (
          <button
            key={v}
            onClick={() => onToggle(v)}
            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
              active
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
            }`}
            data-testid={`${testidPrefix}-${v}`}
          >
            {labelMap ? (labelMap[v] || v) : v}
          </button>
        );
      })}
    </div>
  );
}

export default function BriefBrowser() {
  const [facets, setFacets] = useState(null);
  const [corpus, setCorpus] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedGeoFocus, setSelectedGeoFocus] = useState([]);
  const [tier, setTier] = useState("all");
  const [sort, setSort] = useState("gap_score");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [strongOnly, setStrongOnly] = useState(true);
  const [showHow, setShowHow] = useState(false);
  const [limit] = useState(60);
  const [offset, setOffset] = useState(0);

  // Load facets + corpus stats once
  useEffect(() => {
    axios.get(`${API}/discover/briefs/facets`).then(r => setFacets(r.data)).catch(() => {});
    axios.get(`${API}/discover/corpus/stats`).then(r => setCorpus(r.data)).catch(() => {});
    axios.get(`${API}/corpus/last-refresh`).then(r => setLastRefresh(r.data)).catch(() => {});
  }, []);

  // Reload list when filters change
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (selectedCountries.length) p.set("country", selectedCountries.join(","));
    if (selectedIndustries.length) p.set("industry", selectedIndustries.join(","));
    if (selectedGeoFocus.length) p.set("geo_focus", selectedGeoFocus.join(","));
    if (tier !== "all") p.set("tier", tier);
    if (q.trim()) p.set("q", q.trim());
    p.set("sort", sort);
    p.set("strong_only", strongOnly ? "true" : "false");
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return p.toString();
  }, [selectedCountries, selectedIndustries, selectedGeoFocus, tier, q, sort, strongOnly, limit, offset]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/discover/briefs?${queryString}`);
        if (!cancel) { setItems(r.data.items || []); setTotal(r.data.total || 0); }
      } catch {
        if (!cancel) { setItems([]); setTotal(0); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [queryString]);

  const toggle = (setter) => (v) => setter(prev =>
    prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
  );

  const clearFilters = () => {
    setSelectedCountries([]); setSelectedIndustries([]); setSelectedGeoFocus([]);
    setTier("all"); setQ(""); setOffset(0);
  };

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-2 sm:px-0" data-testid="brief-browser-root">
      {/* Hero */}
      <div className="mb-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-violet-700 mb-1">
          Discover · Proven Winners
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight flex items-center gap-2" data-testid="brief-browser-headline">
          {facets?.strong_count != null
            ? `${facets.strong_count} proven opportunities worth building`
            : "Proven opportunities worth building"}
          <GuideHelpButton feature="discover" />
        </h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-3xl leading-relaxed">
          Each one is a business model that already works elsewhere — with the single best country to launch it in, the companies already proving it, and real research behind every claim. Sorted by how big the opening is.
          {(facets?.total ?? 0) > (facets?.strong_count ?? 0) && (
            <> We also keep <strong>{(facets?.total ?? 0) - (facets?.strong_count ?? 0)} weaker ones</strong> — toggle them on below.</>
          )}
        </p>
        {lastRefresh?.last_refreshed_at && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono text-zinc-500" data-testid="discover-last-refresh">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Corpus last refreshed {new Date(lastRefresh.last_refreshed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            <span className="text-zinc-400">· auto-refresh every {lastRefresh.interval_days}d (next in ~{lastRefresh.next_due_days}d)</span>
          </div>
        )}
      </div>

      {/* HOW THIS WORKS — credibility detail tucked behind a toggle (progressive disclosure) */}
      {(facets?.score_histogram || corpus) && (
        <button
          onClick={() => setShowHow(v => !v)}
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-violet-700 transition-colors"
          data-testid="discover-how-toggle"
          aria-expanded={showHow}
        >
          {showHow ? "▾" : "▸"} How this works <span className="font-normal text-zinc-400">· the data & scoring behind every card</span>
        </button>
      )}

      {/* SCORE DISTRIBUTION HISTOGRAM — quality visible by default */}
      {showHow && facets?.score_histogram && (
        <div className="mb-5 rounded-lg border border-zinc-200 bg-white px-4 py-3" data-testid="score-histogram">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-700">
              Score distribution · {facets.total} distinct opportunities
            </div>
            <label className="flex items-center gap-2 text-[11px] font-mono text-zinc-700 cursor-pointer" data-testid="strong-only-toggle-wrapper">
              <input type="checkbox" checked={strongOnly} onChange={e => { setStrongOnly(e.target.checked); setOffset(0); }} data-testid="strong-only-toggle" />
              <span>Strong only (≥3)</span>
            </label>
          </div>
          <div className="flex items-end gap-1.5 h-16">
            {facets.score_histogram.map(b => {
              const max = Math.max(...facets.score_histogram.map(x => x.count));
              const pct = max > 0 ? (b.count / max) * 100 : 0;
              const isStrong = parseFloat(b.bucket) >= 3 || b.bucket === "6+";
              return (
                <div key={b.bucket} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${b.count} distinct opportunities with strongest-country gap_score ${b.bucket}`}>
                  <div className="text-[9px] font-mono text-zinc-500">{b.count}</div>
                  <div className={`w-full rounded-t transition-colors ${isStrong ? 'bg-violet-500' : 'bg-zinc-300'}`} style={{ height: `${Math.max(pct, 4)}%` }} />
                  <div className={`text-[9px] font-mono ${isStrong ? 'text-violet-700 font-bold' : 'text-zinc-500'}`}>{b.bucket}</div>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
            ▌ <span className="text-violet-700 font-semibold">violet = strong signal (≥3)</span> · gray = modest. Each bar counts <strong>distinct opportunities</strong> by their strongest country — alternates are nested, not double-counted. Honesty about the weak ones is what makes the strong ones credible.
          </p>
        </div>
      )}

      {/* CORPUS STRIP — what's behind the curtain */}
      {showHow && corpus && (
        <div className="mb-5 rounded-lg border border-zinc-200 bg-gradient-to-br from-zinc-900 to-zinc-800 text-white overflow-hidden" data-testid="corpus-stats-strip">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 divide-y sm:divide-y-0 sm:divide-x divide-zinc-700">
            <div className="px-3 py-2.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Startups analyzed</div>
              <div className="text-lg font-bold mt-0.5">{corpus.total_startups.toLocaleString()}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                YC · a16z · Accel · Sequoia
              </div>
            </div>
            <div className="px-3 py-2.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Clusters formed</div>
              <div className="text-lg font-bold mt-0.5">{corpus.total_clusters.toLocaleString()}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">cosine ≥ 0.75</div>
            </div>
            <div className="px-3 py-2.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Gaps detected</div>
              <div className="text-lg font-bold mt-0.5">{corpus.total_gaps.toLocaleString()}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">across {corpus.country_count} countries</div>
            </div>
            <div className="px-3 py-2.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Distinct opportunities</div>
              <div className="text-lg font-bold text-violet-300 mt-0.5 cursor-help"
                title={`${corpus.distinct_opportunities} distinct clusters (one per business archetype). Funnel: ${corpus.funnel?.raw_companies?.toLocaleString()} startups → ${corpus.funnel?.clusters?.toLocaleString()} clusters (cosine ≥ 0.75) → ${corpus.funnel?.raw_gaps?.toLocaleString()} raw (cluster × country) gaps → top-1 country per cluster grounded. ${corpus.total_briefs?.toLocaleString()} grounded briefs total, incl. nested alternate countries.`}>
                {corpus.distinct_opportunities?.toLocaleString() ?? corpus.total_briefs?.toLocaleString()}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{corpus.total_briefs?.toLocaleString()} grounded briefs · incl. alternates</div>
            </div>
            <div className="px-3 py-2.5">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Exited proof</div>
              <div className="text-lg font-bold mt-0.5 cursor-help"
                title={`${corpus.acquired_in_briefs} companies inside our 300-brief clusters have been acquired/exited. That's the strongest possible "this business model worked" signal — someone wrote a check to buy them. (Across the full ${corpus.total_startups.toLocaleString()}-company corpus: ${corpus.acquired_in_corpus} total exits.)`}>
                {corpus.acquired_in_briefs?.toLocaleString() ?? '—'}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">acquired inside 300-brief clusters</div>
            </div>
            <div className="px-3 py-2.5 col-span-2 sm:col-span-1">
              <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Snapshot</div>
              <div className="text-lg font-bold mt-0.5">{corpus.snapshot_date}</div>
              <div className="text-[10px] font-mono text-amber-300 mt-0.5" title="Bi-weekly refresh shipping in V1.1. Until then this is a static snapshot — we'll never lie about that.">
                ⚐ bi-weekly · V1.1
              </div>
            </div>
          </div>
          <div className="px-3 py-2 bg-zinc-950/40 text-[10px] font-mono text-zinc-400 leading-relaxed">
            Pipeline: scrape → Claude Sonnet analysis → Voyage embedding → dedupe → cosine cluster → 30-country gap detect → web-search ground → write brief. <span className="text-zinc-500">Every step shown above is a number you can verify.</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Filters sidebar */}
        <aside className="lg:col-span-3 space-y-4" data-testid="brief-browser-filters">
          <div className="bg-white rounded-lg border border-zinc-200 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-700">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-[10px] font-mono uppercase text-violet-700 hover:underline"
                data-testid="brief-browser-clear-filters"
              >clear</button>
            </div>

            <input
              type="search"
              placeholder="Search opportunities…"
              value={q}
              onChange={e => { setQ(e.target.value); setOffset(0); }}
              className="w-full text-sm px-3 py-2 rounded border border-zinc-200 focus:border-violet-500 focus:outline-none mb-3"
              data-testid="brief-browser-search"
            />

            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1.5">Tier</div>
              <div className="flex gap-1">
                {["all", "free", "pro"].map(t => (
                  <button
                    key={t}
                    onClick={() => { setTier(t); setOffset(0); }}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors flex-1 ${
                      tier === t ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                    }`}
                    data-testid={`brief-browser-tier-${t}`}
                  >{t}</button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1.5">Country</div>
              <FilterChips
                values={facets?.countries || []}
                selected={selectedCountries}
                onToggle={toggle(setSelectedCountries)}
                testidPrefix="brief-filter-country"
              />
            </div>

            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1.5">Industry</div>
              <FilterChips
                values={facets?.industries || []}
                selected={selectedIndustries}
                onToggle={toggle(setSelectedIndustries)}
                labelMap={INDUSTRY_LABELS}
                testidPrefix="brief-filter-industry"
              />
            </div>

            <div>
              <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1.5">Market focus</div>
              <FilterChips
                values={facets?.geo_focus || []}
                selected={selectedGeoFocus}
                onToggle={toggle(setSelectedGeoFocus)}
                testidPrefix="brief-filter-geofocus"
              />
            </div>
          </div>

          <div className="bg-amber-50/60 rounded-lg border border-amber-200 p-3 text-[11px] text-zinc-700 leading-relaxed">
            <div className="font-mono uppercase text-[10px] tracking-[0.18em] text-amber-800 mb-1.5 flex items-center gap-1.5">
              <span className="text-base">⚐</span> The trust moat
            </div>
            <p className="mb-2 text-zinc-700">
              When we couldn&apos;t ground a claim to a real source, we <strong>flagged it instead of fabricating</strong>. That&apos;s the ⚐ tag. It&apos;s how you know everything <em>without</em> the tag is real.
            </p>
            <div className="font-mono uppercase text-[10px] tracking-[0.18em] text-zinc-500 mb-1 mt-3">Legend</div>
            <div>· <span className="font-mono text-violet-700">[1]</span> inline citation → click for source</div>
            <div>· <span className="font-mono text-amber-700">⚐</span> honest deferral → proudly unsourced</div>
            <div>· <span className="font-mono text-emerald-700">free</span> tier = top 20% by gap score</div>
          </div>
        </aside>

        {/* List + Detail */}
        <main className="lg:col-span-9">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-1">
            <div className="text-xs text-zinc-600 font-mono" data-testid="brief-browser-count">
              {loading ? "Finding opportunities…" : `${total} opportunities`}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-600 cursor-pointer" data-testid="strong-only-toggle-wrapper">
                <input type="checkbox" checked={strongOnly} onChange={e => { setStrongOnly(e.target.checked); setOffset(0); }} data-testid="strong-only-toggle" />
                <span>Strong only</span>
              </label>
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-mono uppercase text-zinc-500">Sort</label>
                <select
                  value={sort}
                  onChange={e => { setSort(e.target.value); setOffset(0); }}
                  className="text-xs px-2 py-1 rounded border border-zinc-200 bg-white"
                  data-testid="brief-browser-sort"
                >
                  <option value="gap_score">Opportunity score</option>
                  <option value="composite">Confidence</option>
                </select>
              </div>
            </div>
          </div>

          {openId && (
            <div className="mb-5" data-testid="brief-browser-detail-wrapper">
              <BriefDetailPanel id={openId} onClose={() => setOpenId(null)} />
            </div>
          )}

          <div className="space-y-2" data-testid="brief-browser-list">
            {loading && items.length === 0 && (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} className="bg-white rounded-lg border border-zinc-200 px-4 py-3 animate-pulse" data-testid="brief-skeleton-row">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex gap-2">
                        <div className="h-3 w-10 rounded bg-zinc-200" />
                        <div className="h-3 w-12 rounded bg-zinc-200" />
                      </div>
                      <div className="h-4 w-2/3 rounded bg-zinc-200" />
                      <div className="h-3 w-1/3 rounded bg-zinc-100" />
                      <div className="h-3 w-5/6 rounded bg-zinc-100" />
                    </div>
                    <div className="h-6 w-10 rounded bg-zinc-200 flex-shrink-0" />
                  </div>
                </div>
              ))
            )}
            {items.map(b => (
              <button
                key={b.id}
                onClick={() => { setOpenId(b.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className={`w-full text-left bg-white hover:bg-violet-50 transition-colors rounded-lg border border-zinc-200 hover:border-violet-300 px-4 py-3 ${openId === b.id ? 'ring-2 ring-violet-400' : ''}`}
                data-testid={`brief-row-${b.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <TierBadge tier={b.tier} />
                      <ConfidenceTier composite={b.calibration_composite} />
                      {b.honest_deferral_count > 0 && (
                        <span
                          className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 cursor-help"
                          title={`⚐ ${b.honest_deferral_count} honest deferral${b.honest_deferral_count > 1 ? 's' : ''} — sections where no groundable source was found. We flagged them instead of fabricating. This is the moat.`}
                        >
                          ⚐ {b.honest_deferral_count}
                        </span>
                      )}
                      <span className="text-[9px] font-mono uppercase text-zinc-500">
                        {b.source_count} src
                      </span>
                    </div>
                    <h3 className="font-semibold text-zinc-900 text-[15px] truncate">
                      {b.centroid_name} → {COUNTRY_NAMES[b.missing_country] || b.missing_country}
                    </h3>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                      {INDUSTRY_LABELS[b.industry_bucket] || b.industry_bucket} · {b.cluster_label}
                    </div>
                    {b.the_play && (
                      <div className="text-xs text-zinc-600 mt-1.5 line-clamp-2">{b.the_play}</div>
                    )}
                    {b.alternates && b.alternates.length > 0 && (
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`brief-alternates-${b.id}`}>
                        <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">also viable:</span>
                        {b.alternates.map((alt) => (
                          <span
                            key={alt.id}
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200"
                            title={`${COUNTRY_NAMES[alt.missing_country] || alt.missing_country} · gap ${alt.gap_score}`}
                          >
                            {alt.missing_country} ({alt.gap_score})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-violet-700 leading-none">{b.gap_score.toFixed(2)}</div>
                    <div className="text-[9px] font-mono uppercase text-zinc-500 mt-0.5">score</div>
                  </div>
                </div>
              </button>
            ))}
            {!loading && items.length === 0 && (
              <div className="text-center py-10 text-zinc-500 text-sm" data-testid="brief-browser-empty">
                No briefs match these filters. <button onClick={clearFilters} className="text-violet-700 underline">Reset</button>.
              </div>
            )}
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-4 px-1">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="text-xs font-mono px-3 py-1.5 rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40"
                data-testid="brief-browser-prev-page"
              >‹ prev</button>
              <span className="text-[11px] font-mono text-zinc-500">
                {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="text-xs font-mono px-3 py-1.5 rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40"
                data-testid="brief-browser-next-page"
              >next ›</button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
